import { jsx } from 'react/jsx-runtime';
import React__default from 'react';
import { ResizeableEdges } from '../components/ResizeableEdges.js';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';

const MIN_WIDTH = 500;
const MOVEABLE_AREA_EDGE_OFFSET = 10;
const useResourceMoveAndResize = (isFullscreen, sidebarEl, rootRef, contentWrapperEl, minHeight, sidebarOpened, paddingOffsetY) => {
  const { isDesktopView } = useResponsive();
  const [isMoving, setIsMoving] = React__default.useState(false);
  const [moveable, setMoveable] = React__default.useState({
    position: null,
    size: null,
    moved: false
  });
  const moved = moveable.moved && isDesktopView && !isFullscreen;
  const minPosition = React__default.useMemo(() => {
    if (!rootRef) return {
      x: 0,
      y: 0
    };
    const { left, top } = rootRef.getBoundingClientRect();
    return {
      x: left + MOVEABLE_AREA_EDGE_OFFSET,
      y: top + MOVEABLE_AREA_EDGE_OFFSET
    };
  }, [
    rootRef
  ]);
  const onPositionMouseDown = React__default.useCallback((e) => {
    if (!sidebarEl || !isDesktopView) return;
    const target = e.target;
    const targetWithAttachedListener = e.currentTarget;
    if (target !== targetWithAttachedListener) {
      return;
    }
    if (targetWithAttachedListener.getAttribute("data-movable-area") !== "true") return;
    const onMouseMove = (e2) => {
      setMoveable((moveable2) => {
        const { position, size } = moveable2;
        const { innerWidth, innerHeight } = window;
        const { width, height } = size ?? {
          width: 0,
          height: 0
        };
        const x = position?.x ?? minPosition.x;
        const y = position?.y ?? minPosition.y;
        const newX = x + e2.movementX;
        const newY = y + e2.movementY;
        const sidebarRect = sidebarEl.getBoundingClientRect();
        const sidebarLeft = sidebarRect.left ?? innerWidth;
        const constrainX = Math.min(Math.max(newX, minPosition.x), sidebarLeft - width - MOVEABLE_AREA_EDGE_OFFSET);
        const constrainY = Math.min(Math.max(newY, minPosition.y), innerHeight - height - MOVEABLE_AREA_EDGE_OFFSET);
        return {
          ...moveable2,
          position: {
            x: constrainX,
            y: constrainY
          },
          moved: true
        };
      });
      e2.preventDefault();
    };
    setIsMoving(true);
    const detachEventListeners = () => {
      setIsMoving(false);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", detachEventListeners);
      document.removeEventListener("blur", detachEventListeners);
      document.removeEventListener("focusout", detachEventListeners);
    };
    const rect = contentWrapperEl?.getBoundingClientRect();
    if (rect) {
      setMoveable((moveable2) => ({
        ...moveable2,
        position: {
          x: rect.x,
          y: rect.y
        },
        size: {
          width: rect.width,
          height: rect.height
        },
        moved: true
      }));
    }
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", detachEventListeners);
    document.addEventListener("blur", detachEventListeners);
    document.addEventListener("focusout", detachEventListeners);
  }, [
    sidebarEl,
    isDesktopView,
    contentWrapperEl,
    minPosition.x,
    minPosition.y
  ]);
  const onResizeMouseDown = React__default.useCallback((e) => {
    const from = e.currentTarget.dataset.from;
    if (!from) return;
    if (!sidebarEl || !isDesktopView) return;
    const onMouseMove = (e2) => {
      const altPressed = e2.altKey;
      setMoveable(({ position, size }) => {
        let newWidth = size?.width ?? 0;
        let newHeight = size?.height ?? 0;
        let newX = position?.x ?? minPosition.x;
        let newY = position?.y ?? minPosition.y;
        const sidebarRect = sidebarEl.getBoundingClientRect();
        const rightEdge = sidebarRect.left || window.innerWidth;
        if (from.includes("left")) {
          const potentialWidth = newWidth - e2.movementX * (altPressed ? 2 : 1);
          const potentialX = newX + e2.movementX;
          if (potentialWidth > MIN_WIDTH && potentialX >= minPosition.x) {
            newWidth = potentialWidth;
            newX = potentialX;
          }
        } else if (from.includes("right")) {
          const potentialWidth = newWidth + e2.movementX * (altPressed ? 2 : 1);
          if (potentialWidth > MIN_WIDTH && altPressed) {
            newX = Math.min(Math.max(newX - e2.movementX, minPosition.x), rightEdge);
          }
          newWidth = potentialWidth;
        }
        if (from.includes("top")) {
          const potentialHeight = newHeight - e2.movementY * (altPressed ? 2 : 1);
          const potentialY = newY + e2.movementY;
          if (potentialHeight > minHeight && potentialY >= minPosition.y) {
            newHeight = potentialHeight;
            newY = potentialY;
          }
        } else if (from.includes("bottom")) {
          const potentialHeight = newHeight + e2.movementY * (altPressed ? 2 : 1);
          if (potentialHeight > minHeight && altPressed) {
            newY = Math.min(Math.max(newY - e2.movementY, minPosition.y), window.innerHeight);
          }
          newHeight = potentialHeight;
        }
        newX = Math.min(Math.max(newX, minPosition.x), rightEdge);
        newY = Math.min(Math.max(newY, minPosition.y), window.innerHeight);
        newWidth = Math.min(Math.max(newWidth, MIN_WIDTH), rightEdge - newX - MOVEABLE_AREA_EDGE_OFFSET);
        newHeight = Math.min(Math.max(newHeight, minHeight), window.innerHeight - newY - MOVEABLE_AREA_EDGE_OFFSET);
        return {
          position: {
            x: newX,
            y: newY
          },
          size: {
            width: newWidth,
            height: newHeight
          },
          moved: true
        };
      });
      e2.preventDefault();
    };
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    const rect = contentWrapperEl?.getBoundingClientRect();
    if (rect) setMoveable((moveable2) => ({
      ...moveable2,
      position: {
        x: rect.x,
        y: rect.y
      },
      moved: true
    }));
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    e.preventDefault();
  }, [
    contentWrapperEl,
    isDesktopView,
    minHeight,
    minPosition.x,
    minPosition.y,
    sidebarEl,
    setMoveable
  ]);
  const resizeUiHandlers = React__default.useMemo(() => {
    if (isDesktopView && !isFullscreen) {
      return /* @__PURE__ */ jsx(ResizeableEdges, {
        onResizeMouseDown
      });
    }
  }, [
    isDesktopView,
    isFullscreen,
    onResizeMouseDown
  ]);
  const updateSize = React__default.useCallback(() => {
    setMoveable((moveable2) => {
      if (!moveable2.position || !sidebarEl) return moveable2;
      const { position, size } = moveable2;
      let newX = position.x;
      let newY = position.y;
      let newWidth = Math.max(MIN_WIDTH, size?.width ?? 0);
      let newHeight = Math.max(minHeight, size?.height ?? 0);
      const sidebarRect = sidebarEl.getBoundingClientRect();
      const sidebarLeft = sidebarRect.left ?? window.innerWidth;
      newX = Math.max(Math.min(newX, sidebarLeft - newWidth), 0);
      newY = Math.max(Math.min(newY, window.innerHeight - newHeight), 0);
      if (newX + newWidth >= sidebarLeft) {
        newWidth = sidebarLeft - newX;
      }
      if (newY + newHeight >= window.innerHeight) {
        newHeight = window.innerHeight - newY;
      }
      return {
        ...moveable2,
        position: {
          x: newX,
          y: newY
        },
        size: {
          width: newWidth,
          height: newHeight
        }
      };
    });
  }, [
    minHeight,
    sidebarEl
  ]);
  const resetPositionAndSize = React__default.useCallback(() => {
    setMoveable((moveable2) => {
      if (!moveable2.position) return moveable2;
      return {
        ...moveable2,
        position: null,
        size: null,
        moved: false
      };
    });
  }, [
    setMoveable
  ]);
  React__default.useEffect(() => {
    if (!contentWrapperEl || !sidebarEl || !isDesktopView) return;
    window.addEventListener("resize", updateSize);
    return () => {
      window.removeEventListener("resize", updateSize);
    };
  }, [
    contentWrapperEl,
    isDesktopView,
    sidebarEl,
    updateSize
  ]);
  React__default.useEffect(() => {
    if (!contentWrapperEl || moveable.moved) return;
    const updateSize2 = () => {
      const rect = contentWrapperEl.getBoundingClientRect();
      setMoveable((moveable2) => ({
        ...moveable2,
        size: {
          width: rect.width,
          height: rect.height - paddingOffsetY
        },
        position: {
          x: rect.x,
          y: rect.y
        }
      }));
    };
    updateSize2();
    const observer = new ResizeObserver(updateSize2);
    observer.observe(contentWrapperEl);
    const observer2 = new MutationObserver(updateSize2);
    observer2.observe(contentWrapperEl, {
      attributes: true,
      attributeFilter: [
        "style"
      ]
    });
    window.addEventListener("scroll", updateSize2);
    window.addEventListener("resize", updateSize2);
    return () => {
      observer.disconnect();
      observer2.disconnect();
      window.removeEventListener("scroll", updateSize2);
      window.removeEventListener("resize", updateSize2);
    };
  }, [
    contentWrapperEl,
    moveable.moved,
    paddingOffsetY
  ]);
  React__default.useEffect(() => {
    updateSize();
  }, [
    sidebarOpened,
    updateSize
  ]);
  return React__default.useMemo(() => [
    {
      ...moveable,
      isMoving,
      onPositionMouseDown,
      onResizeMouseDown,
      moved,
      minPosition,
      resizeUiHandlers,
      updateSize,
      resetPositionAndSize
    },
    setMoveable
  ], [
    isMoving,
    moveable,
    onPositionMouseDown,
    onResizeMouseDown,
    moved,
    minPosition,
    resizeUiHandlers,
    updateSize,
    resetPositionAndSize
  ]);
};

export { MIN_WIDTH, useResourceMoveAndResize };
//# sourceMappingURL=useMoveAndResize.js.map
